import { Injectable, computed, inject, signal, effect, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { HuntStateService } from './hunt-state.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { defer } from 'rxjs';
import { BadgeVariant } from '@hyperkit';
import { findRouteWithParam } from '../helpers/utils';
import {
  Enums,
  Environment,
} from '@cyborg-monorepo/hunter/common';
import { NavigationEnd } from '@angular/router';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
@Injectable({ providedIn: 'root' })
export class QueryActiveHuntPackageService {
  private readonly huntState = inject(HuntStateService);
  private readonly router = inject(Router);


  readonly packageId = toSignal(
    defer(() => {
      const rootRoute = inject(ActivatedRoute);
      const initial = findRouteWithParam(rootRoute, 'packageId')?.snapshot.paramMap.get('packageId') ?? null;

      return this.router.events.pipe(
        filter(e => e instanceof NavigationEnd),
        startWith(null),
        switchMap(() => {
          const found = findRouteWithParam(rootRoute, 'packageId');
          return found?.paramMap ?? rootRoute.paramMap;
        }),
        map(params => params.get('packageId')),
        startWith(initial)
      );
    }),
    { initialValue: null }
  );

  clearActivePackage(): void {
    const route = findRouteWithParam(this.router.routerState.root, 'packageId');
    if (route) {
      this.router.navigate(['../'], { relativeTo: route });
    } else {
      console.warn('Could not resolve route to clear package');
    }
  }  

  
  isActivePackage = (pkgId: string): boolean => {
    return this.packageId() === pkgId;
  };
  readonly hunt = computed(() => this.huntState.hunt());
  readonly huntPackages = computed(() => {
    const hunt = this.hunt();
    return hunt?.huntPackages ?? [];
  });
  readonly isHydrated = computed(() => !!this.hunt() && !!this.activePackage());
  readonly huntPackageTitles = computed(() =>
    this.huntPackages().map(pkg => pkg.title)
);

readonly activePackage = computed(() => {
  const hunt = this.hunt();
  const id = this.packageId();
  
  if (!hunt || !hunt.huntPackages || !id) {
    if (!hunt) console.warn('❗ Hunt is not yet hydrated');
    if (!id) console.warn('❗ packageId not yet available from route');
    return null;
  }
  
  const match = hunt.huntPackages.find(pkg => pkg.UUID === id);
  if (!match) console.warn('❌ No matching package found for ID:', id);
  return match ?? null;
});

readonly logPackages = computed(() => {
  return this.huntPackages().filter(pkg => {
    console.log('does this match', pkg.UUID, '=', this.packageId());
    return pkg.UUID === this.packageId();
  });
});


readonly content = computed(() => this.activePackage()?.content);
readonly title = computed(() => this.activePackage()?.title ?? 'Untitled');
readonly author = computed(() => this.activePackage()?.author ?? 'Unknown');
readonly status = computed(() => this.activePackage()?.status ?? 'Unknown');
readonly description = computed(() => this.activePackage()?.description ?? 'N/A');
readonly validation = computed(() => this.activePackage()?.validation ?? 'N/A');
readonly completedDate = computed(() => this.activePackage()?.completed_date);
readonly UUID = computed(() => this.activePackage()?.UUID ?? null);
readonly createdDate = computed(() => this.activePackage()?.created_date);
readonly lastUpdatedDate = computed(() => this.activePackage()?.last_updated);
readonly severity = computed(() => this.activePackage()?.severity ?? 'N/A');

readonly severityVariant = computed<BadgeVariant>(() => {
  const severity = this.severity();
  switch (severity) {
    case Enums.SeverityEnum.LOW: return 'success';
    case Enums.SeverityEnum.MEDIUM: return 'warning';
    case Enums.SeverityEnum.HIGH: return 'error';
    case Enums.SeverityEnum.CRITICAL: return 'error';
    default: return 'neutral';
  }
});
readonly notes = computed(() => this.activePackage()?.analyst_notes ?? 'N/A');


readonly threatNames = computed(() => this.activePackage()?.context?.threat_names ?? []);
readonly threatCategories = computed(() => this.activePackage()?.context?.threat_categories ?? []);
readonly sourceCountries = computed(() => this.activePackage()?.context?.source_countries.join(', ') ?? []);
readonly actors = computed(() => this.activePackage()?.context?.actors ?? 'N/A');
readonly targetOses = computed(() => this.activePackage()?.context?.target_oses.join(', ') ?? []);

readonly dependencies = computed(() => this.activePackage()?.dependencies ?? []);
readonly diamondModel = computed(() => this.activePackage()?.mapping?.diamond_model ?? 'N/A');
readonly killChains = computed(() => this.activePackage()?.mapping?.kill_chains.join(', ') ?? []);
readonly mitreAttackTactics = computed(() => this.activePackage()?.mapping?.mitre?.mitre_attack_tactics.join(', ') ?? []);
readonly mitreAttackTechniques = computed(() => this.activePackage()?.mapping?.mitre?.mitre_attack_techniques.join(', ') ?? []);

readonly tags = {
  threatTypes: computed(() => this.activePackage()?.tags?.threat_types ?? []),
  threatCategories: computed(() => this.activePackage()?.tags?.threat_categories ?? []),
  attackSurfaces: computed(() => this.activePackage()?.tags?.attack_surfaces ?? []),
  targetOses: computed(() => this.activePackage()?.tags?.target_oses ?? []),
};

readonly complexityScore = computed(() => this.activePackage()?.complexity?.score ?? 0);

// environments + tools + queryies
readonly selectedEnvironment = signal<Environment | null>(null);
readonly getAllEnvironments = computed(() => this.activePackage()?.analysis ?? []);
readonly getNumEnvironments = computed(() => this.activePackage()?.analysis.length ?? 0);
readonly getToolsBySelectedEnvironment = computed(() => this.selectedEnvironment()?.tools ?? []);
readonly getAllTools = computed(() =>
  this.activePackage()?.analysis?.flatMap(env => env.tools ?? []) ?? []
);
readonly toolCategories = computed(() => {
  const tools = this.getToolsBySelectedEnvironment();
  const categories = tools.flatMap(tool =>
    tool.generatedContent.Hunt.map(mapping => mapping.category)
  );
  return Array.from(new Set(categories));
  });

setEnvironment(environment: Environment) {
  this.selectedEnvironment.set(environment);
}


  readonly queryTable = computed(() => this.activePackage()?.query_logic?.table ?? []);
  readonly mitigationRecommendations = computed(() => this.activePackage()?.response_actions.mitigation_recommendations ?? 'N/A');

  readonly selectedCategory = signal<string | null>(null);

  readonly filteredTools = computed(() => {
    const selected = this.selectedCategory();
    if (!selected) return this.getAllTools();
    return this.getAllTools().filter(tool =>
      tool.generatedContent.Hunt.some(mapping => mapping.category === selected)
    );
  });

  setCategory(category: string) {
    this.selectedCategory.set(category);
  }


  clearCategoryFilter() {
    this.selectedCategory.set(null);
  }

  huntPackageById = (id: string) => {
    return computed(() =>
      this.huntPackages().find(pkg => pkg.UUID === id) ?? null
    );
  };

  getAnalysis = (id: string) => computed(() => this.huntPackageById(id)()?.analysis ?? []);
  allTools = (id: string) => computed(() => this.getAnalysis(id)().flatMap(env => env.tools ?? []));
  getTool = (pkgId: string, toolId: string) => computed(() => this.allTools(pkgId)().find(tool => tool.id === toolId) ?? null);
  getTitle = (id: string) => computed(() => this.huntPackageById(id)()?.title ?? 'Untitled');
  getAuthor = (id: string) => computed(() => this.huntPackageById(id)()?.author ?? 'Unknown');
  getStatus = (id: string) => computed(() => this.huntPackageById(id)()?.status ?? 'Unknown');
  getCompletedDate = (id: string) => computed(() => this.huntPackageById(id)()?.completed_date ?? null);
  getComplexityScore = (id: string) => computed(() => this.huntPackageById(id)()?.complexity?.score ?? 0);
  getResearchEffort = (id: string) => computed(() => this.huntPackageById(id)()?.complexity?.research ?? 0);
  getEnrichmentLevel = (id: string) => computed(() => this.huntPackageById(id)()?.complexity?.enrichment ?? 0);
  getReferenceLinks = (id: string) => computed(() => [
    ...(this.huntPackageById(id)()?.references?.blog_links ?? []),
    ...(this.huntPackageById(id)()?.references?.general ?? [])
  ]);
  getTagGoals = (id: string) => computed(() => this.huntPackageById(id)()?.tags?.goals ?? []);
  getTagTools = (id: string) => computed(() => this.huntPackageById(id)()?.tags?.tools ?? []);
  getTagActors = (id: string) => computed(() => this.huntPackageById(id)()?.tags?.actors ?? []);
  getQueryCondition = (id: string) => computed(() => this.huntPackageById(id)()?.query_logic?.condition ?? '');
  getQueryTables = (id: string) => computed(() => this.huntPackageById(id)()?.query_logic?.table ?? []);
  getRunbook = (id: string) => computed(() => this.huntPackageById(id)()?.response_actions?.analyst_runbook ?? '');
  getSeverity = (id: string) => computed(() => this.huntPackageById(id)()?.severity ?? 'Unknown');
  getSigma = (id: string) => computed(() => this.huntPackageById(id)()?.sigma ?? '');



  constructor() {
    effect(() => {
      console.log('💧 Hydrated: is hydrated', this.isHydrated());   
      console.log('🐛 Debug: hunt loaded?', !!this.hunt());
      console.log('📦 packageId:', this.packageId());
      console.log('📦 activePackage:', this.activePackage());
      console.log('HUNT STATE STATIC:', this.huntState.hunt());
    });
  }
  

}
